import { Controller } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { Select } from "ui/select";
import { TextArea } from "ui/text-area";
import { Button, LoadingButton } from "ui/button";
import SmsSimulatorSimulationCard from "containers/sms-simulator/sms-simulator-simulator/components/simulated-sms-card/sms-simulator-simulation-card";

import { useSmsSimulatorForm } from "containers/sms-simulator/sms-simulator-simulator/hooks/use-sms-simulator-form";

import { useSmsSimulatorContext } from "containers/sms-simulator/sms-simulator.context";

import { SmsConversation } from "containers/sms-simulator/sms-simulator.types";

interface SimulatorFormProps {
  isSimulatorMinimized: boolean;
}

const SmsSimulatorSimulationsForm = ({ isSimulatorMinimized }: SimulatorFormProps) => {
  const { currentSimulation, clients } = useSmsSimulatorContext();

  const {
    control,
    handleSubmit,
    errors,
    isValid,
    isLoading,
    isTransitioning,
    onSubmit,
    updateDraft,
    handleRefresh,
  } = useSmsSimulatorForm();

  const renderChatBoxes = () => {
    if (!currentSimulation) {
      return null;
    }

    const conversations = currentSimulation.simulationData || [];

    return conversations.map((conversation: SmsConversation) => {
      return (
        <div
          key={conversation.persona.id}
          className={twMerge(
            "transition-opacity duration-300 ease-in-out",
            isTransitioning ? "opacity-0" : "opacity-100",
          )}>
          <SmsSimulatorSimulationCard
            personaBadgeProps={conversation.persona}
            conversation={conversation.mockMessages}
          />
        </div>
      );
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <div
        className={twMerge(
          "transition-all duration-500 ease-in-out overflow-hidden",
          isSimulatorMinimized ? "max-h-0 opacity-0" : "max-h-[800px] opacity-100",
        )}>
        <div className="flex gap-6 justify-between">
          <div className="flex flex-col gap-3 w-1/2">
            <Controller
              name="client"
              control={control}
              render={({ field: { onChange, value, ...field } }) => (
                <Select
                  label={<span className="font-bold">Client</span>}
                  selectName="client"
                  placeholder="Select a client"
                  options={clients}
                  required
                  errMessage={errors?.client?.message as string}
                  ariaLabel="Select a client"
                  value={value || null}
                  onChange={(selectedOption: any) => {
                    onChange(selectedOption);
                    updateDraft("client", selectedOption);
                  }}
                  {...field}
                />
              )}
            />
            <Controller
              name="testSmsText"
              control={control}
              render={({ field: { onBlur, ...field } }) => (
                <TextArea
                  label={<span className="font-bold">Test SMS Text</span>}
                  placeholder="Enter your SMS message here..."
                  required
                  errMessage={errors?.testSmsText?.message as string}
                  rows={5}
                  onBlur={(e) => {
                    onBlur();
                    updateDraft("testSmsText", e.target.value);
                  }}
                  inputSx={{ paddingTop: 1, paddingBottom: 1.25 }}
                  {...field}
                />
              )}
            />
          </div>
          <div className="flex flex-col gap-4 w-1/2">
            <Controller
              name="systemPrompt"
              control={control}
              render={({ field: { onBlur, ...field } }) => (
                <TextArea
                  label={<span className="font-bold">System Prompt</span>}
                  placeholder="Enter your System Prompt here..."
                  required
                  errMessage={errors?.systemPrompt?.message as string}
                  rows={9}
                  onBlur={(e) => {
                    onBlur();
                    updateDraft("systemPrompt", e.target.value);
                  }}
                  {...field}
                />
              )}
            />
          </div>
        </div>
        <div className="flex gap-3 mt-4 mb-1">
          <LoadingButton
            type="submit"
            disabled={!isValid}
            state={isLoading ? "loading" : "default"}
            loadingMessage="Simulating...">
            Simulate
          </LoadingButton>
          <Button variant="contained" onClick={handleRefresh}>
            Refresh
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-4">{renderChatBoxes()}</div>
    </form>
  );
};

export default SmsSimulatorSimulationsForm;
